/**
 * **Which vacancy this is, when the title does not say** — one wording, shared by §6.3's filter and
 * §6.5's consideration block (2026-11-03, TASK-187/TASK-188, ADR 0020 decision 9).
 *
 * ⚠️ **It exists because a title is not an identifier, and that was measured rather than feared.**
 * The first real ADP import wrote 95 requisitions into the dev tenant and the titles repeat:
 * *Manual QA Engineer* ×8, *Firmware Engineer* ×7, *Embedded Engineer* ×7, *Business Analyst* ×5.
 * §6.3's vacancy filter offered **96 options** with nothing to tell them apart, and a person card
 * rendered the same consideration line twice — *"Embedded Engineer · New · Opened Nov 3, 2026"* —
 * for two genuinely different requisitions (1061 and 1055). Neither screen could name which.
 *
 * ⚠️ **One module rather than two renderings, and that is the criterion rather than tidiness.**
 * BUG-088 is this project's written lesson about one thing named two ways in two places: both
 * spellings were true, and the defect was that a reader comparing the two screens could not tell
 * whether they were looking at the same fact. So the **order of the facts**, the **words** and the
 * **separator** live here, and both screens ask for them.
 *
 * ⚠️ **The two facts are the ones that *identify* the requisition, and nothing that describes the
 * job.** Location is excluded — a requisition genuinely has several, and a list turns a one-line
 * identity strip into a paragraph — and so is department, which says what the work is rather than
 * which run this is. The question both screens are answering is *whose vacancy is this*.
 *
 * ⚠️ **The hiring manager is personal data of the customer's own employee.** It is tenant-scoped,
 * a candidate's erasure does not clear it (it is not the candidate's data), and it must reach no
 * log line and no error message. Nothing in this module does anything with the value except put it
 * in a sentence.
 */

/**
 * The pair, named in the client's own vocabulary rather than in either payload's.
 *
 * The wire spells them `requisition_number` / `hiring_manager` on `RoleOptionOut` and
 * `role_requisition_number` / `role_hiring_manager` on `ConsiderationOnPersonOut` — the `role_`
 * prefix being the backend's rule that these are the **vacancy's** facts carried on a consideration
 * — so each caller maps its own payload in and this module is spared knowing which screen it is on.
 */
export interface VacancyIdentity {
  requisitionNumber: string;
  hiringManager: string;
}

/**
 * The `t` both screens already hold. Typed structurally and deliberately narrow: this module may
 * read the catalogue and may not reach i18next for anything else.
 */
type Translate = (key: string, values?: Record<string, string>) => string;

/**
 * The one separator, `·`, with one space on each side — §6.8's `Locations` cell and the provenance
 * line use the same character for the same job, which is a list of short facts that are not a
 * sentence. A comma would read as prose and a `|` as a table nobody drew.
 */
export const VACANCY_FACT_SEPARATOR = ' · ';

/**
 * The facts, in order, with the blanks dropped.
 *
 * ⚠️ **Always, never only when a title repeats.** A label whose shape depended on what else is in
 * the list would make one vacancy read two ways on two pages — and on §6.5 there is no list to
 * compare against at all, so the rule has to be a property of the vacancy rather than of its
 * neighbours.
 *
 * **A blank is an absence**, which is what every integration key on this surface is on a
 * hand-created vacancy (`""`, never `null`). So a vacancy Panel's own screens created contributes
 * **no** facts and reads as its bare title, with no label, no dash and no empty scaffolding — and
 * an imported requisition that carries no manager (one of the 95) reads exactly the same way.
 */
export function vacancyFacts(vacancy: VacancyIdentity, t: Translate): string[] {
  // The number first: it is ADP's own identifier, the thing a recruiter quotes out loud, and the
  // one of the two that is unique. The manager follows, because several requisitions share one.
  const facts = [
    requisitionFact(vacancy, t),
    vacancy.hiringManager === ''
      ? ''
      : t('candidates:vacancy.hiringManager', { name: vacancy.hiringManager }),
  ];

  return facts.filter((fact) => fact !== '');
}

/**
 * The requisition on its own, `''` when there is none.
 *
 * It is a function rather than two call sites spelling the key out, because the trigger label below
 * needs this one fact without the other and a second `t('candidates:vacancy.requisition')` in this
 * module is the next copy that drifts.
 */
function requisitionFact(vacancy: VacancyIdentity, t: Translate): string {
  return vacancy.requisitionNumber === ''
    ? ''
    : t('candidates:vacancy.requisition', { number: vacancy.requisitionNumber });
}

/**
 * The facts as one line — §6.5's strip, beneath the vacancy's title.
 *
 * Empty when there are none, which is the signal to render nothing at all rather than an empty
 * paragraph: §6.8 takes the same position one screen over for a requisition with no locations.
 */
export function vacancyIdentityLine(vacancy: VacancyIdentity, t: Translate): string {
  return vacancyFacts(vacancy, t).join(VACANCY_FACT_SEPARATOR);
}

/**
 * The title **with** the facts — §6.3's filter option, which has one string and no second line.
 *
 * The title leads because it is what a reader scans for; the facts are what they fall back on when
 * two titles are the same. A hand-created vacancy's option is therefore the bare title and nothing
 * else, with no separator left over.
 */
export function vacancyOptionLabel(
  vacancy: VacancyIdentity & { title: string },
  t: Translate,
): string {
  return [vacancy.title, ...vacancyFacts(vacancy, t)].join(VACANCY_FACT_SEPARATOR);
}

/**
 * The same vacancy as §6.3's **collapsed trigger** says it — *"Requisition 1061 · Firmware
 * Engineer"* — and the one place the order of those two facts is decided.
 *
 * ⚠️ **It exists because the label above does not survive the control it is read in** (D-1,
 * 2026-11-04, found by `ui-reviewer` against the real catalogue). The trigger is 224 px with about
 * **174 px** of text room; **every one** of the tenant's 95 labels is wider than that, and for
 * **27** the title alone already overflows. Selecting requisition 1089 and selecting 1068 produced
 * **byte-identical** screenshots — *"Software Engineer · Requi…"* — of two filters that return
 * different candidates. The label was not merely long: it was identical for exactly the options the
 * whole sprint existed to tell apart.
 *
 * **The number leads here and the title leads in the list, and that is the decision.** Truncation
 * eats the end, so whatever must survive has to be at the front — and what must survive is the one
 * fact that is **unique**. In the open list nothing is truncated and nothing has to survive, so the
 * title leads there because a title is what a reader scans 96 rows for. Both strings are built from
 * the same facts, in the same words, with the same separator, in this module: **one wording, two
 * orders, each argued** — which is what BUG-088 asks for, rather than two spellings nobody chose.
 *
 * ⚠️ **The hiring manager is deliberately dropped, and that is about what is *painted* rather than
 * about disclosure.** It is the longest of the three facts and the least distinguishing (several
 * requisitions share one), so in ~174 px it costs the title and buys nothing. Nothing is narrowed
 * by leaving it out: `RoleOptionOut` sends it to exactly the callers it sent it to before, the open
 * list renders it, and the trigger's own `title` tooltip carries the whole label — so this is not
 * a second, client-side reading of ADR 0020 decision 9, which is about routes and groups. What
 * changes is only which three facts are worth 174 px.
 *
 * **With no requisition number there is nothing to lead with**, so the trigger reads the bare title
 * — which is exactly what a hand-created vacancy's option already is, trigger and list alike.
 */
export function vacancyTriggerLabel(
  vacancy: VacancyIdentity & { title: string },
  t: Translate,
): string {
  const requisition = requisitionFact(vacancy, t);

  return requisition === ''
    ? vacancy.title
    : [requisition, vacancy.title].join(VACANCY_FACT_SEPARATOR);
}
